import React, { useCallback, useEffect, useState } from 'react';
import { usePage } from '../../hooks/usePage';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { complianceApi, ListHealth, ReviewItem } from '../../api/compliance';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { Table } from '../../components/ui/Table';
import { Drawer } from '../../components/ui/Drawer';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';

const MIN_REASON = 20;

/**
 * Screening review — potential matches, and the health of the lists behind them.
 *
 * ## The comparison is the screen
 *
 * A reviewer decides whether the person PayIQ screened is the person a
 * regulator designated. That decision needs both records side by side — name,
 * date of birth, nationality, and which list the designation came from —
 * because deciding from a score alone is not review, it is agreement.
 *
 * ## The two outcomes are deliberately asymmetric
 *
 * Clearing is a routine judgement; most potential matches are ordinary people
 * sharing a common Nigerian surname. Confirming freezes the subject
 * immediately, without notice to them (TPPA s.54), and starts a 24-hour STR
 * clock — so it sits behind a separate permission and an explicit
 * confirmation naming what will happen.
 *
 * ## List health lives here, not on a settings page
 *
 * A list that silently stopped refreshing keeps returning "clear" and looks
 * healthy. Putting freshness next to the review queue means the person who
 * would notice an empty queue is the same person who can see why it is empty.
 */
export const ScreeningReview: React.FC = () => {
  usePage('Screening Review');
  const { can } = useAuth();
  const toast = useToast();

  const [items, setItems] = useState<ReviewItem[]>([]);
  const [health, setHealth] = useState<ListHealth[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ReviewItem | null>(null);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pending, lists] = await Promise.all([
        complianceApi.pendingScreening(),
        complianceApi.listHealth(),
      ]);
      setItems(pending.items);
      setHealth(lists);
    } catch {
      toast.error('Could not load the screening queue.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  async function clear() {
    if (!selected || reason.trim().length < MIN_REASON) return;
    setBusy(true);
    try {
      await complianceApi.clearScreening(selected.result.id, reason.trim());
      toast.success('Recorded as a false positive.');
      setSelected(null);
      setReason('');
      void load();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Could not clear the match.';
      toast.error(message);
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!selected) return;
    setBusy(true);
    try {
      await complianceApi.confirmScreening(selected.result.id);
      toast.success('Match confirmed. Subject restricted and an STR drafted.');
      setConfirming(false);
      setSelected(null);
      void load();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Could not confirm the match.';
      toast.error(message);
    } finally {
      setBusy(false);
    }
  }

  const stale = health.filter((h) => h.stale);

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      {stale.length > 0 && (
        // Loud, because the failure it describes is silent: screening keeps
        // returning "clear" against a corpus that stopped updating.
        <Card>
          <div className="rounded bg-red-50 p-3 text-sm text-red-800">
            <strong>{stale.length} sanctions list(s) are not refreshing.</strong>{' '}
            Screening continues to run and will keep returning “clear”, so an
            empty queue right now proves nothing.
            <ul className="mt-2 list-inside list-disc">
              {stale.map((h) => (
                <li key={h.name}>
                  {h.name} —{' '}
                  {h.staleDays === null
                    ? 'never refreshed'
                    : `${h.staleDays} days since last refresh`}
                </li>
              ))}
            </ul>
          </div>
        </Card>
      )}

      <Card>
        <h2 className="mb-3 text-sm font-semibold">Potential matches awaiting review</h2>
        <Table
          data={items.map((i) => ({ ...i, id: i.result.id }))}
          emptyMessage="Nothing awaiting review."
          onRowClick={(row) => {
            const item = items.find((i) => i.result.id === row.id);
            if (item) { setSelected(item); setReason(''); }
          }}
          columns={[
            { key: 'name', header: 'Screened name', render: (r) => r.result.screenedName },
            { key: 'score', header: 'Score', render: (r) => `${r.result.matchScore}` },
            { key: 'context', header: 'Context', render: (r) => r.result.context.replace('_', ' ') },
            { key: 'matched', header: 'Designation', render: (r) => r.matched?.primaryName ?? <span className="text-gray-400">—</span> },
            { key: 'when', header: 'Screened', render: (r) => new Date(r.result.createdAt).toLocaleDateString() },
          ]}
        />
      </Card>

      <Card>
        <h2 className="mb-3 text-sm font-semibold">Sanctions list health</h2>
        <Table
          data={health.map((h) => ({ ...h, id: h.name }))}
          emptyMessage="No lists configured. Screening has nothing to compare against."
          columns={[
            { key: 'name', header: 'List', render: (h) => h.name },
            { key: 'entries', header: 'Entries', render: (h) => h.entryCount.toLocaleString() },
            { key: 'version', header: 'Version', render: (h) => h.version ?? '—' },
            {
              key: 'refreshed',
              header: 'Last refreshed',
              render: (h) => (
                <span className={h.stale ? 'font-semibold text-red-600' : ''}>
                  {h.lastRefreshedAt
                    ? `${h.staleDays}d ago`
                    : 'Never'}
                </span>
              ),
            },
            { key: 'status', header: 'Status', render: (h) => (h.stale ? <Badge variant="danger">Stale</Badge> : <Badge variant="success">Fresh</Badge>) },
          ]}
        />
      </Card>

      <Drawer
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        title="Review potential match"
      >
        {selected && (
          <div className="space-y-4 text-sm">
            {/* Side by side, because deciding from a score alone is agreement,
                not review. */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded bg-gray-50 p-3">
                <div className="text-xs font-semibold uppercase text-gray-500">Our customer</div>
                <div className="mt-1 font-medium">{selected.result.screenedName}</div>
                <div className="mt-2 text-xs text-gray-500">
                  {selected.result.subjectType} · screened {selected.result.context.replace('_', ' ')}
                </div>
              </div>
              <div className="rounded bg-gray-50 p-3">
                <div className="text-xs font-semibold uppercase text-gray-500">Designation</div>
                {selected.matched ? (
                  <>
                    <div className="mt-1 font-medium">{selected.matched.primaryName}</div>
                    <dl className="mt-2 space-y-1 text-xs text-gray-600">
                      <div>DOB: {selected.matched.dateOfBirth ?? '—'}</div>
                      <div>Nationality: {selected.matched.nationality ?? '—'}</div>
                      <div>Reference: {selected.matched.designationRef ?? '—'}</div>
                      {selected.matched.aliases.length > 0 && (
                        <div>Also known as: {selected.matched.aliases.join(', ')}</div>
                      )}
                    </dl>
                  </>
                ) : (
                  <div className="mt-1 text-gray-400">
                    No designation recorded — the entry may have been removed by
                    a list refresh. The screening record is retained regardless.
                  </div>
                )}
              </div>
            </div>

            <div>
              Match score: <strong>{selected.result.matchScore}</strong>
            </div>

            {can('compliance.review') && (
              <div className="space-y-2 border-t pt-3">
                <label className="block text-xs font-semibold uppercase text-gray-500">
                  Not the same person?
                </label>
                <textarea
                  className="w-full rounded border px-2 py-1"
                  rows={3}
                  placeholder="What distinguishes our customer from the designated person?"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <div className="text-xs text-gray-500">
                  {reason.trim().length}/{MIN_REASON} characters minimum
                </div>
                <Button
                  disabled={busy || reason.trim().length < MIN_REASON}
                  onClick={clear}
                >
                  Record as false positive
                </Button>
              </div>
            )}

            {can('compliance.close') && (
              <div className="space-y-2 border-t pt-3">
                <div className="rounded bg-red-50 p-3 text-xs text-red-800">
                  Confirming restricts the subject immediately and drafts a
                  suspicious transaction report against a 24-hour deadline. The
                  customer is not told, and must not be.
                </div>
                <Button variant="danger" disabled={busy} onClick={() => setConfirming(true)}>
                  Confirm this is the designated person
                </Button>
              </div>
            )}
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        isOpen={confirming}
        title="Confirm sanctions match"
        message="The subject will be restricted immediately and an STR drafted against a 24-hour statutory deadline. Do not contact the customer about this — disclosing it is a criminal offence."
        confirmLabel="Confirm match"
        onConfirm={confirm}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
};
